import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DashboardDialogFramePart, DialogAction, DialogContext, DialogService } from '@eforall/dashboard';
import { DialogMode, StaffRoleId } from '@interfaces';
import { TabStripModule } from '@progress/kendo-angular-layout';
import { EngagementEditorDialog, EngagementEditorDialogData } from '../engagement-editor/engagement-editor.dialog';
import { FollowUpEditorDialog, FollowUpEditorDialogData, FollowUpViewerDialog, FollowUpViewerDialogData } from '../follow-up';
import { EngagementViewerDialogService } from './engagement-viewer.service';
import { EngagementViewerInteractionsPart } from './interactions/interactions.part';
import { EngagementViewerOverviewPart } from './overview/overview.part';

export interface EngagementViewerDialogData {
	engagementId: number;
	staffRoleId?: StaffRoleId;
}

@Component({
	selector: 'programs-engagement-viewer-dialog',
	standalone: true,
	imports: [
		DashboardDialogFramePart,
		TabStripModule,
		EngagementViewerOverviewPart,
		EngagementViewerInteractionsPart
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	templateUrl: './engagement-viewer.dialog.html',
})

export class EngagementViewerDialog implements OnInit {

	public dialogRef = inject(MatDialogRef<EngagementViewerDialog>);
	public dialogContext: DialogContext<EngagementViewerDialogData> = inject(MAT_DIALOG_DATA);

	private readonly dialogService = inject(DialogService);
	private readonly service = inject(EngagementViewerDialogService);


	async ngOnInit(): Promise<void> {
		if (this.dialogContext.data?.engagementId) {
			await this.service.getEngagementDetails(this.dialogContext.data.engagementId);
		}
	}

	public actions = computed<DialogAction[]>(() => {

		const data = this.dialogContext.data;
		if (!data) return [];

		const createOrClosedFollowUp = () => {
			actions.push({
				id: this.service.engagementDetails()?.closedUTC ? 'createFollowUp' : 'closeWithFollowUp',
				enabled: signal(true),
				visible: signal(true),
				text: computed(() => this.service.engagementDetails()?.closedUTC ? 'Create Follow Up' : 'Close with Follow Up'),
				cssClass: signal('btn-standard'),
				linkType: "callback",
				willCloseDialog: false,
				callback: async () => {
					const dialogData: FollowUpEditorDialogData = {
						mode: DialogMode.ADD,
						engagementId: data.engagementId
					};
					const dialogAction = await this.dialogService.showCustom(FollowUpEditorDialog, { data: dialogData }, 650, 750);
					if (dialogAction?.callbackResult) {
						await this.service.getEngagementDetails(data.engagementId);
					}
				}
			});
		}

		const followUpViewerDialog = (followUpId: number) => {
			actions.push({
				id: 'viewFollowUp',
				enabled: signal(true),
				visible: signal(true),
				text: signal('View Follow Up'),
				cssClass: signal('btn-standard'),
				linkType: "callback",
				willCloseDialog: false,
				callback: async () => {
					const dialogData: FollowUpViewerDialogData = {
						followUpId,
						staffRoleId: data.staffRoleId
					};
					await this.dialogService.showCustom(FollowUpViewerDialog, { data: dialogData }, 600, 600);
					await this.service.getEngagementDetails(data.engagementId);
				}
			});
		}

		const engagmentDetails = this.service.engagementDetails();
		const actions: DialogAction[] = [];
		if (engagmentDetails) {
			if (engagmentDetails.closedUTC) {
				actions.push({
					id: 'reopenEngagement',
					enabled: signal(true),
					visible: signal(true),
					text: signal('Reopen'),
					cssClass: signal('btn-standard'),
					linkType: "callback",
					willCloseDialog: false,
					callback: async () => {
						await this.service.reOpenEngagement(data.engagementId);
						await this.service.getEngagementDetails(data.engagementId);
					}
				});
			} else {
				actions.push({
					id: 'editEngagement',
					enabled: signal(true),
					visible: signal(true),
					text: signal('Edit'),
					cssClass: signal('btn-standard'),
					linkType: "callback",
					willCloseDialog: false,
					callback: async () => {
						const dialogData: EngagementEditorDialogData = {
							mode: DialogMode.EDIT,
							engagementId: data.engagementId,
						};
						await this.dialogService.showCustom(EngagementEditorDialog, { data: dialogData }, 650, 800);
						await this.service.getEngagementDetails(data.engagementId);
					}
				});

				actions.push({
					id: 'closeEngagement',
					enabled: signal(true),
					visible: signal(true),
					text: signal('Close Engagement'),
					cssClass: signal('btn-standard'),
					linkType: "callback",
					willCloseDialog: false,
					callback: async () => {
						await this.service.closeEngagement(data.engagementId);
						await this.service.getEngagementDetails(data.engagementId);
					}
				});

			}

			if (engagmentDetails.followUpId) {
				followUpViewerDialog(engagmentDetails.followUpId);
			} else {
				createOrClosedFollowUp();
			}
		}

		actions.push(
			{
				id: 'no',
				enabled: signal(true),
				visible: signal(true),
				text: signal('Cancel'),
				cssClass: signal('btn-standard'),
				linkType: "no-action",
				willCloseDialog: true,
			}
		);

		return actions;
	});
}